import axios from 'axios';

export interface AlpacaNewsItem {
  id: string;
  title: string;
  summary: string;
  source: string;
  url: string;
  image?: string;
  publishedAt: string;
  symbols: string[];
  sentiment?: number;
}

export interface AlpacaNewsResponse {
  symbol: string;
  news: AlpacaNewsItem[];
  count: number;
}

class AlpacaNewsService {
  private baseUrl: string;

  constructor() {
    // Use the same base URL pattern as other services
    this.baseUrl = '/api/market'; // This will proxy to our backend
  }

  async getSymbolNews(symbol: string, limit: number = 10, daysBack: number = 7): Promise<AlpacaNewsItem[]> {
    try {
      console.log(`AlpacaNewsService: Fetching news for ${symbol} (limit: ${limit}, days: ${daysBack})`);
      
      const response = await axios.get<AlpacaNewsResponse>(`${this.baseUrl}/news/${symbol}`, {
        params: {
          limit: Math.min(limit, 50), // Alpaca max is 50
          days_back: Math.min(daysBack, 30) // Backend max is 30
        },
        timeout: 10000 // 10 second timeout
      });

      if (response.data && response.data.news) {
        console.log(`AlpacaNewsService: Successfully fetched ${response.data.news.length} articles for ${symbol}`);
        return response.data.news;
      } else {
        console.warn(`AlpacaNewsService: Invalid response format for ${symbol}:`, response.data);
        return [];
      }
    } catch (error: any) {
      console.error(`AlpacaNewsService: Error fetching news for ${symbol}:`, error);
      
      if (error.response) {
        // Backend returned an error
        console.error(`AlpacaNewsService: Backend error ${error.response.status}:`, error.response.data);
      } else if (error.request) {
        // Network error
        console.error('AlpacaNewsService: Network error - no response received');
      } else {
        // Request setup error
        console.error('AlpacaNewsService: Request setup error:', error.message);
      }
      
      // Return empty array instead of throwing to allow graceful fallback
      return [];
    }
  }

  async getNewsWithFallback(symbol: string, limit: number = 10): Promise<AlpacaNewsItem[]> {
    try {
      // Try to get fresh news from Alpaca
      const alpacaNews = await this.getSymbolNews(symbol, limit, 7);
      
      if (alpacaNews && alpacaNews.length > 0) {
        return alpacaNews;
      }
      
      // If no recent news, try extending the lookback period
      console.log(`AlpacaNewsService: No recent news for ${symbol}, trying extended period`);
      const extendedNews = await this.getSymbolNews(symbol, limit, 14);
      
      return extendedNews || [];
    } catch (error) {
      console.error(`AlpacaNewsService: Fallback failed for ${symbol}:`, error);
      return [];
    }
  }
}

// Create and export a singleton instance
export const alpacaNewsService = new AlpacaNewsService(); 